<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.qualitative_reports', 2),
                        href: null,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div class="flex space-x-2">
                        <a
                            :href="meta.links.print_index"
                            target="_blank"
                            class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            ><!---->
                            <!---->
                            <svg viewBox="0 0 20 20" fill="currentColor" class="print w-4 h-4">
                                <path
                                    fill-rule="evenodd"
                                    d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                                    clip-rule="evenodd"
                                ></path></svg
                        ></a>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.other_groups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        <FormSelect
                            id="trimester"
                            v-model="form.trimester"
                            :options="options.trimesters"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.trimester" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 flex justify-end">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.teachers', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.students', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in group.students"
                                :key="student.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <InertiaLink :href="student.links.show" class="text-indigo-500">
                                        {{ student.full_name }}
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.latest_date || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div
                                        class="shrink-0 w-2.5 h-2.5 rounded-full"
                                        :class="student.has_record_in_trimester ? 'bg-green-400' : 'bg-gray-400'"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-gray-400" />
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex space-x-1 justify-end"
                                >
                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="student.links.student_show"
                                        :title="$t('messages.show')"
                                        :disabled="!student.links.student_show"
                                        v-if="student.links.student_show"
                                    />
                                    <InertiaSquareLinkButton
                                        element="a"
                                        action="print"
                                        :href="student.links.student_print_show"
                                        :title="$t('messages.print')"
                                        :disabled="!student.links.student_print_show"
                                        v-if="student.links.student_print_show"
                                    />
                                    <InertiaSquareLinkButton
                                        action="chart"
                                        href="/"
                                        :title="$t('messages.stats')"
                                        disabled
                                    />
                                    <InertiaSquareLinkButton
                                        action="add"
                                        :href="student.links.student_create"
                                        :title="$t('messages.create')"
                                    />
                                    <InertiaSquareLinkButton
                                        action="comment"
                                        :href="student.links.student_comment_index"
                                        :title="$t('messages.comment')"
                                    />
                                    <InertiaSquareLinkButton
                                        action="list"
                                        :href="student.links.student_list"
                                        :title="$t('messages.list')"
                                    />
                                    <InertiaSquareLinkButton
                                        element="a"
                                        action="template"
                                        :href="student.links.student_print_create"
                                        :title="$t('messages.template')"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        FormError,
        FormSelect,
        FormLabel,
        PanelPage,
        InertiaSquareLinkButton,
        ActivityMenuOptions,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'qualitativeReportCategories', 'filters', 'options', 'group'],
    data() {
        return {
            form: this.$inertia.form({
                trimester: this.filters.trimester,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
